/**
 * Price formatting — and the one rule that governs it (01 §7, D7).
 *
 * The studio may show a **material price from the store, with the date it was captured**, and
 * an **"Estimated labor from"** figure. It may not show a total, a subtotal, a final price or
 * a quote. Andrew prices the job after the visit, and a number on a screen that looks like a
 * price is a number a homeowner will hold him to.
 *
 * There is deliberately no `total()` in this module. Adding one is the change that would make
 * the rule breakable, and the forbidden-token scan (`make copy-rules-test`, mirrored in `tests/visual/screens.spec.ts`) fails on it.
 */

import { shortDay } from './datetime'
import type { CartLine, LaborFrom, Money } from './wstypes'

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})

const UNIT_LABEL: Record<string, string> = {
  each: 'each',
  sqft: 'per sq ft',
  lnft: 'per linear ft',
  hour: 'per hour',
}

function money(value: Money | null | undefined): string {
  if (!value) return '—'
  return USD.format(Number(value.amount))
}

function unitLabel(unit: string): string {
  return UNIT_LABEL[unit] ?? unit
}

/** "$799.00 each" — the store's price, per the unit it is sold in. */
export function materialPrice(value: Money | null | undefined): string {
  if (!value) return 'Price on request'
  return `${money(value)} ${unitLabel(value.unit)}`
}

/**
 * "Estimated labor from $6.50 per linear ft", or "Estimated on site".
 *
 * A rate Andrew has not set is null, and null renders as the honest sentence rather than as
 * $0.00 — 02 §0.2 is explicit that inventing a figure would be inventing a price.
 */
export function laborFrom(labor: LaborFrom | null | undefined): string {
  if (!labor || labor.amount === null) return 'Estimated on site'
  return `Estimated labor from ${USD.format(Number(labor.amount))} ${unitLabel(labor.unit)}`
}

/** "3 sq ft" / "1 each" — a quantity with its unit, for a cart line. */
export function quantity(line: Pick<CartLine, 'qty' | 'unit'>): string {
  const value = Number(line.qty)
  const rounded = Number.isInteger(value) ? value : Number(value.toFixed(1))
  if (line.unit === 'each') return `${rounded} ${rounded === 1 ? 'item' : 'items'}`
  return `${rounded} ${line.unit === 'sqft' ? 'sq ft' : line.unit === 'lnft' ? 'linear ft' : 'hours'}`
}

/** "as of 20 Aug" — the honesty half of a material price (01 §7). */
export function priceAsOf(iso: string | null | undefined, timeZone: string): string {
  return iso ? shortDay(iso, timeZone) : ''
}

/** Copy for a quantity the studio guessed rather than measured (07 §5). */
export function qtyNote(line: Pick<CartLine, 'qty_method' | 'qty_confidence'>): string | null {
  if (line.qty_method === 'user') return null
  const confidence = Number(line.qty_confidence ?? 0)
  if (confidence >= 0.6) return 'Rough size — Andrew measures on site'
  return 'Rough size — add the room dimensions for a closer number'
}
